import { forwardRef, useId } from "react";
import { useObjectRef, chain } from "react-aria";
import {
  type InputProps as InputPrimitiveProps,
  TextField as TextFieldPrimitive,
  type TextFieldProps as TextFieldPrimitiveProps,
} from "react-aria-components";
import { useControlledState } from "@react-stately/utils";
import { type PropsWithOptionalTestID } from "@boi/components-core";

import * as S from "./TextInputV2.styled";
import { ReactAriaProviders } from "../ReactAriaProviders";
import { InputWrapper } from "../InputWrapper";
import { InputAlerts, type InputAlertsProps } from "../InputAlerts";
import { InputLabelTopV2, type InputLabelTopV2Props } from "../InputLabelTopV2";
import { OuterFieldV2, type OuterFieldV2Props } from "../OuterFieldV2";
import { CharactersCounter } from "../CharactersCounter";

export type TextInputV2Props = PropsWithOptionalTestID &
  Pick<InputPrimitiveProps, "placeholder"> &
  Omit<TextFieldPrimitiveProps, "children"> &
  Pick<OuterFieldV2Props, "startSlot" | "endSlot"> & {
    labelProps?: Omit<InputLabelTopV2Props, "children">;
    alerts?: InputAlertsProps["alerts"];
    withCharacterCounter?: boolean;
  };

const TextInputV2 = forwardRef<HTMLInputElement, TextInputV2Props>(
  (props, forwardedRef) => {
    const {
      labelProps,
      alerts,
      id,
      placeholder,
      startSlot,
      endSlot,
      withCharacterCounter,
      onChange,
      testID = "text-input",
      ...otherProps
    } = props;

    const { maxLength } = otherProps;

    const ref = useObjectRef(forwardedRef);

    const fallbackID = useId();
    const startSlotID = useId();

    const externallyDescribedBy =
      [props["aria-describedby"], startSlot ? startSlotID : undefined]
        .filter(Boolean)
        .join(" ") || undefined;

    const [inputValue, setInputValue] = useControlledState(
      props.value,
      props.defaultValue ?? ""
    );

    const handleChange = chain(onChange, setInputValue);

    function focusElement() {
      ref.current?.focus();
    }

    return (
      <ReactAriaProviders
        fieldID={id || fallbackID}
        externallyLabelledBy={props["aria-labelledby"]}
        externallyDescribedBy={externallyDescribedBy}
        labelProps={labelProps}
        alerts={alerts}
      >
        {({ labelledBy, describedBy }) => (
          <InputWrapper
            alert={alerts && <InputAlerts alerts={alerts} />}
            gap={
              withCharacterCounter && maxLength !== undefined ? "4px" : "8px"
            }
          >
            <S.CharactersCounterContainer>
              <InputLabelTopV2 {...labelProps}>
                <TextFieldPrimitive
                  {...otherProps}
                  id={id || fallbackID}
                  aria-labelledby={labelledBy}
                  aria-describedby={describedBy}
                  onChange={handleChange}
                  data-testid={testID}
                >
                  <OuterFieldV2
                    startSlot={
                      startSlot && (
                        <S.SlotWrapper id={startSlotID}>
                          {startSlot}
                        </S.SlotWrapper>
                      )
                    }
                    endSlot={endSlot}
                    focusElement={focusElement}
                  >
                    <S.Input ref={ref} placeholder={placeholder} />
                  </OuterFieldV2>
                </TextFieldPrimitive>
              </InputLabelTopV2>

              {withCharacterCounter && maxLength !== undefined && (
                <CharactersCounter
                  length={inputValue.length}
                  maxLength={maxLength}
                  testID={`${testID}-character-counter`}
                />
              )}
            </S.CharactersCounterContainer>
          </InputWrapper>
        )}
      </ReactAriaProviders>
    );
  }
);

TextInputV2.displayName = "TextInputV2";

export default TextInputV2;
